import React, { useEffect, useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getOrderLineUnitPrice, buildOrderItemOptionSnapshot } from "@/components/pos/orderCartUtils.js";

const getMenuIdFromMenu = (menu) =>
  menu?.id || menu?.Id || menu?.menuId || menu?.MenuId;

const getMenuOptions = (menu) => {
  const options = menu?.menuOptions || menu?.MenuOptions || [];
  return Array.isArray(options) ? options : [];
};

const getOptionId = (option) => option?.id || option?.Id;

const getOptionName = (option) =>
  option?.optionName || option?.OptionName || "Option";

const getOptionPrice = (option) =>
  Number(option?.optionPrice ?? option?.OptionPrice ?? 0);

const getExtraId = (extra) => extra?.id || extra?.Id;

const getExtraName = (extra) => extra?.name || extra?.Name || "Extra";

const getExtraPrice = (extra) => Number(extra?.price ?? extra?.Price ?? 0);

export function getNormalizedMenuOptions(menu) {
  return getMenuOptions(menu).map((option) => ({
    id: getOptionId(option),
    optionName: getOptionName(option),
    optionPrice: getOptionPrice(option),
  }));
}

export function buildCartItemKey({
  menuId,
  orderItemMenuOptions,
  orderItemOption,
  orderItemExtras,
}) {
  const optionsKey =
    Array.isArray(orderItemMenuOptions) && orderItemMenuOptions.length > 0
      ? [...orderItemMenuOptions]
          .map((option) => {
            const quantity = Number(option.quantity ?? 0);
            if (quantity <= 0) return null;
            return `${option.menuOptionId || option.id || option.Id || ""}:${quantity}`;
          })
          .filter(Boolean)
          .sort()
          .join(",")
      : orderItemOption?.menuOptionId || "";

  const extrasKey = Array.isArray(orderItemExtras)
    ? [...orderItemExtras]
        .map((extra) => {
          const quantity = Number(extra.quantity ?? 0);
          if (quantity <= 0) return null;
          return `${extra.extrasId || extra.ExtrasId || ""}:${quantity}`;
        })
        .filter(Boolean)
        .sort()
        .join(",")
    : "";

  return `${menuId}|${optionsKey}|${extrasKey}`;
}

export function menuHasOptions(menu) {
  return getMenuOptions(menu).length > 0;
}

function QuantityStepper({ quantity, onDecrease, onIncrease }) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-lg"
        onClick={onDecrease}
      >
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-6 text-center text-sm font-semibold">{quantity}</span>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-lg"
        onClick={onIncrease}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

function QuantityOptionRow({
  name,
  unitPrice,
  quantity,
  onDecrease,
  onIncrease,
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-3 ${
        quantity > 0 ? "border-primary bg-primary/5" : "hover:border-primary/40"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{name}</p>
        <p className="text-xs text-muted-foreground">
          + Rs {Number(unitPrice || 0).toFixed(2)} each
        </p>
      </div>

      <QuantityStepper
        quantity={quantity}
        onDecrease={onDecrease}
        onIncrease={onIncrease}
      />
    </div>
  );
}

export default function AddMenuItemModal({
  open,
  onOpenChange,
  menu,
  extras = [],
  itemName,
  itemPrice,
  itemImage,
  onConfirm,
}) {
  const options = useMemo(() => getNormalizedMenuOptions(menu), [menu]);
  const [optionQuantities, setOptionQuantities] = useState({});
  const [extraQuantities, setExtraQuantities] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;

    setOptionQuantities({});
    setExtraQuantities({});
    setQuantity(1);
    setDescription("");
  }, [open, menu, options]);

  const selectedMenuOptions = useMemo(
    () =>
      options
        .map((option) => {
          const optionId = String(option.id);
          const optionQuantity = Number(optionQuantities[optionId] ?? 0);
          if (optionQuantity <= 0) return null;

          return {
            optionName: option.optionName,
            optionPrice: Number(option.optionPrice || 0),
            menuOptionId: option.id,
            quantity: optionQuantity,
          };
        })
        .filter(Boolean),
    [options, optionQuantities],
  );

  const selectedExtras = useMemo(
    () =>
      extras
        .map((extra) => {
          const extraId = String(getExtraId(extra));
          const extraQuantity = Number(extraQuantities[extraId] ?? 0);
          if (extraQuantity <= 0) return null;

          return {
            name: getExtraName(extra),
            price: getExtraPrice(extra),
            extrasId: getExtraId(extra),
            quantity: extraQuantity,
          };
        })
        .filter(Boolean),
    [extras, extraQuantities],
  );

  const unitPrice = useMemo(() => {
    return getOrderLineUnitPrice({
      itemPrice,
      orderItemMenuOptions: selectedMenuOptions,
      orderItemExtras: selectedExtras,
    });
  }, [itemPrice, selectedMenuOptions, selectedExtras]);

  const lineTotal = unitPrice * Number(quantity || 1);

  const updateOptionQuantity = (optionId, nextQuantity) => {
    const id = String(optionId);
    setOptionQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, Number(nextQuantity) || 0),
    }));
  };

  const updateExtraQuantity = (extraId, nextQuantity) => {
    const id = String(extraId);
    setExtraQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, Number(nextQuantity) || 0),
    }));
  };

  const handleConfirm = () => {
    const orderItemMenuOptions = selectedMenuOptions;
    const orderItemOption = buildOrderItemOptionSnapshot(orderItemMenuOptions);

    onConfirm({
      itemName,
      itemPrice: Number(itemPrice || 0),
      menuId: getMenuIdFromMenu(menu),
      image: itemImage,
      quantity: Number(quantity || 1),
      description: description.trim(),
      orderItemMenuOptions,
      orderItemOption,
      orderItemExtras: selectedExtras,
    });

    onOpenChange(false);
  };

  if (!menu) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add item</DialogTitle>
          <DialogDescription>
            Set quantities for menu options and add-ons. Leave any item at 0 if
            you do not want it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex gap-3 rounded-xl border bg-muted/20 p-3">
            {itemImage ? (
              <img
                src={itemImage}
                alt={itemName}
                className="h-16 w-16 shrink-0 rounded-lg object-cover"
              />
            ) : null}

            <div className="min-w-0 flex-1">
              <p className="font-semibold">{itemName}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Base Rs {Number(itemPrice || 0).toFixed(2)}
              </p>
            </div>
          </div>

          {options.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Menu options{" "}
                <span className="font-normal text-muted-foreground">
                  (optional, min 0)
                </span>
              </Label>
              <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                {options.map((option) => {
                  const optionId = String(option.id);
                  const optionQuantity = Number(optionQuantities[optionId] ?? 0);

                  return (
                    <QuantityOptionRow
                      key={optionId}
                      name={option.optionName}
                      unitPrice={option.optionPrice}
                      quantity={optionQuantity}
                      onDecrease={() =>
                        updateOptionQuantity(optionId, optionQuantity - 1)
                      }
                      onIncrease={() =>
                        updateOptionQuantity(optionId, optionQuantity + 1)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          {extras.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Add-ons{" "}
                <span className="font-normal text-muted-foreground">
                  (optional, min 0)
                </span>
              </Label>
              <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                {extras.map((extra) => {
                  const extraId = String(getExtraId(extra));
                  const extraQuantity = Number(extraQuantities[extraId] ?? 0);

                  return (
                    <QuantityOptionRow
                      key={extraId}
                      name={getExtraName(extra)}
                      unitPrice={getExtraPrice(extra)}
                      quantity={extraQuantity}
                      onDecrease={() =>
                        updateExtraQuantity(extraId, extraQuantity - 1)
                      }
                      onIncrease={() =>
                        updateExtraQuantity(extraId, extraQuantity + 1)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium">Item quantity</Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-10 w-10 rounded-xl"
                onClick={() =>
                  setQuantity((prev) => Math.max(1, Number(prev || 1) - 1))
                }
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-10 text-center text-base font-semibold">
                {quantity}
              </span>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-10 w-10 rounded-xl"
                onClick={() => setQuantity((prev) => Number(prev || 1) + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-item-notes" className="text-sm font-medium">
              Special instructions
            </Label>
            <Textarea
              id="add-item-notes"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional notes for the kitchen"
              className="min-h-20 rounded-xl"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-4 py-3">
            <span className="text-sm text-muted-foreground">Line total</span>
            <span className="text-base font-bold">
              Rs {lineTotal.toFixed(2)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" className="rounded-xl" onClick={handleConfirm}>
            Add to order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
